import { SessionNotificationItemRecipientKindEnum, SessionNotificationItemStatusEnum } from "@simbelapp/order-sdk";
import { BookingEventsEnum } from "~/utils/enums/bookings-events.enums";
import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import type { ISessionNotificationEventTag } from "~/utils/interfaces/session-notifications-interfaces";

/** Branch picked on the « A qui souhaitez-vous envoyer une notification ? » screen (AC-ENVOI.12). */
export enum SessionNotificationDeliveryEnum {
  PARTICIPANTS = "participants",
  MANAGERS = "managers",
  TRAINERS = "trainers",
  TRAINING_PARTNER = "training_partner",
}

/** Messages orders answers a refused partner send with (400), matched to pick the French copy. */
export enum PartnerNotificationErrorEnum {
  NO_RECIPIENT = "No recipient address for the partner notification",
  DOCUMENTS_NOT_IN_SESSION = "Some documents do not belong to this session",
  TEMPLATE_NOT_FOUND = "Template not found for this company",
}

/** Messages orders answers a refused trainer send with (400), matched to pick the French copy. */
export enum TrainerNotificationErrorEnum {
  NO_TRAINER_SELECTED = "Select at least one trainer",
  TRAINER_NOT_ASSIGNED = "Recipients must be active internal trainers assigned to this session",
  TRAINER_WITHOUT_EMAIL = "Every selected trainer must have an email address",
}

/** What made orders send a trainer notification (`cause` of the journal entry). */
export enum TrainerNotificationCauseEnum {
  MANUAL = "manual",
  TIMESLOT_UPDATED = "timeslot_updated",
  ASSIGNMENT_REMOVED = "assignment_removed",
}

/** Why orders sent a trainer notification by itself, as the notification detail tells it. */
export const TRAINER_NOTIFICATION_CAUSE_LABEL = new Map<string, string>([
  [
    TrainerNotificationCauseEnum.TIMESLOT_UPDATED,
    "Mise à jour automatique : la date, l'heure ou le lieu de l'étape a changé.",
  ],
  [
    TrainerNotificationCauseEnum.ASSIGNMENT_REMOVED,
    "Annulation automatique : le formateur a été retiré de l'étape, ou l'étape a été supprimée.",
  ],
]);

export const SESSION_NOTIFICATION_STATUS_LABEL: Record<SessionNotificationItemStatusEnum, string> = {
  [SessionNotificationItemStatusEnum.Scheduled]: "Programmé",
  [SessionNotificationItemStatusEnum.Sending]: "En cours d’envoi",
  [SessionNotificationItemStatusEnum.Sent]: "Envoyé",
  [SessionNotificationItemStatusEnum.Failed]: "Échec",
  [SessionNotificationItemStatusEnum.Cancelled]: "Annulé",
};

/** « Programmé » takes the purple of the scheduled surveys in the Évaluations tab. */
export const SESSION_NOTIFICATION_STATUS_TAG_TYPE: Record<SessionNotificationItemStatusEnum, ColorsTypeEnum> = {
  [SessionNotificationItemStatusEnum.Scheduled]: ColorsTypeEnum.LIGHT_PURPLE,
  [SessionNotificationItemStatusEnum.Sending]: ColorsTypeEnum.LIGHT_ORANGE,
  [SessionNotificationItemStatusEnum.Sent]: ColorsTypeEnum.LIGHT_GREEN,
  [SessionNotificationItemStatusEnum.Failed]: ColorsTypeEnum.LIGHT_RED,
  [SessionNotificationItemStatusEnum.Cancelled]: ColorsTypeEnum.LIGHT_GREY,
};

export const SESSION_NOTIFICATION_RECIPIENT_LABEL: Record<SessionNotificationItemRecipientKindEnum, string> = {
  [SessionNotificationItemRecipientKindEnum.Attendees]: "Participants",
  [SessionNotificationItemRecipientKindEnum.Managers]: "Managers",
  [SessionNotificationItemRecipientKindEnum.Partner]: "Prestataire",
  [SessionNotificationItemRecipientKindEnum.Trainer]: "Formateurs",
};

export const SESSION_NOTIFICATION_RECIPIENT_TAG_TYPE: Record<SessionNotificationItemRecipientKindEnum, ColorsTypeEnum> =
  {
    [SessionNotificationItemRecipientKindEnum.Attendees]: ColorsTypeEnum.GREY,
    [SessionNotificationItemRecipientKindEnum.Managers]: ColorsTypeEnum.LIGHT_PURPLE,
    [SessionNotificationItemRecipientKindEnum.Partner]: ColorsTypeEnum.LIGHT_BLUE,
    [SessionNotificationItemRecipientKindEnum.Trainer]: ColorsTypeEnum.LIGHT_BLUE,
  };

/** Icons of the delivery chooser cards (AC-ENVOI.12). */
export const SESSION_NOTIFICATION_DELIVERY_ICON: Record<SessionNotificationDeliveryEnum, string> = {
  [SessionNotificationDeliveryEnum.PARTICIPANTS]: "student",
  [SessionNotificationDeliveryEnum.MANAGERS]: "group",
  [SessionNotificationDeliveryEnum.TRAINERS]: "hand_and_hat",
  [SessionNotificationDeliveryEnum.TRAINING_PARTNER]: "indiv",
};

/** Addresses shown inline in the Emails column, the rest folded into the « +N » pill: a second would hide behind the ellipsis. */
export const SESSION_NOTIFICATION_VISIBLE_RECIPIENTS = 1;

/** Label of the attendee-list snapshot in the attachments column (AC-ENVOI.26). */
export const ATTENDEE_LIST_ATTACHMENT_LABEL = "Liste des participants";

/** The calendar invitation, as the attachments column shows it for attendees and trainers alike. */
export const CALENDAR_INVITATION_TAG: ISessionNotificationEventTag = {
  value: "Invitation agenda",
  type: ColorsTypeEnum.GREY,
  logo: "date",
};

/** Events whose attachments column shows a fixed tag instead, keyed by the SDK's string `eventName`. */
export const SESSION_NOTIFICATION_EVENT_TAG = new Map<string, ISessionNotificationEventTag>([
  [BookingEventsEnum.SEND_ICS, CALENDAR_INVITATION_TAG],
  [BookingEventsEnum.DIRECT_PEDAGOGICAL_REPORT, { value: "Enquête à chaud", type: ColorsTypeEnum.GREY }],
  [BookingEventsEnum.DELAYED_PEDAGOGICAL_REPORT, { value: "Enquête à froid", type: ColorsTypeEnum.GREY }],
]);

/** Sender of every send no user triggered by hand: rule-driven ones and system ones (AC-REGLE.8). */
export const AUTOMATIC_SENDER_LABEL = "Automatique";

/** Sender of a rule-driven row, clickable to open the rule it comes from (AC-REGLE.7). */
export const RULE_SENDER_LABEL = "Règle automatique";
